import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeftIcon, ArrowRightIcon } from 'lucide-react';
import { fleet, getVehicle } from '../data/fleet';
import { EndNote } from '../components/EndNote';
import { SeoHead } from '../components/SeoHead';
export function FleetVehicle() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const vehicle = slug ? getVehicle(slug) : undefined;
  if (!vehicle) {
    return <div className="mx-auto max-w-page px-6 py-24 md:px-10" data-id="element-211" data-mp="1axrsb2frltl">
        <h1 className="font-display text-4xl font-bold uppercase leading-[0.9] md:text-6xl" data-id="element-212" data-mp="3sgz8az5677mm">
          We can&apos;t find
          <br data-id="element-213" data-mp="a3hubmlumajp" />
          that vehicle
        </h1>
        <Link to="/fleet" className="mt-7 inline-flex items-center gap-2 border-b-2 border-lilac pb-1 text-[0.7rem] uppercase tracking-micro transition-colors hover:text-lilac" data-id="element-214" data-mp="5brifc79jeq6">
          <ArrowLeftIcon size={14} data-id="element-215" data-mp="39py49wp70lw7" />
          Back to the fleet
        </Link>
      </div>;
  }
  const index = fleet.findIndex(item => item.slug === vehicle.slug);
  const next = fleet[(index + 1) % fleet.length];
  return <article data-id="element-216" data-mp="1ev7k2hqt67qb">
      <SeoHead title={`${vehicle.name} (${vehicle.length}) — ${vehicle.use} Truck | Breezy Experiential`} description={`${vehicle.summary} Available in ${vehicle.markets.join(', ')}. Owned and operated by Breezy Experiential.`} data-id="element-217" data-mp="1euy2nijtx8w8" />

      <div className="mx-auto max-w-page px-6 pt-10 md:px-10 md:pt-14" data-id="element-218" data-mp="qyjzwhxv8hax">
        <Link to="/fleet" className="inline-flex items-center gap-2 text-[0.65rem] uppercase tracking-micro text-muted transition-colors hover:text-lilac" data-id="element-219" data-mp="r0eny54fopj8">
          <ArrowLeftIcon size={13} data-id="element-220" data-mp="10v6j72ajn0au" />
          Our fleet
        </Link>
      </div>

      <header className="mx-auto max-w-page px-6 pb-10 pt-6 md:px-10 md:pb-14" data-id="element-221" data-mp="15wccp2c12rlf">
        <p className="text-[0.65rem] uppercase tracking-micro text-lilac" data-id="element-222" data-mp="2ccg0pja43i9i">
          {vehicle.use}
        </p>
        <h1 className="mt-4 font-display text-4xl font-bold uppercase leading-[0.9] md:text-7xl" data-id="element-223" data-mp="2h9oihirclka8">
          {vehicle.name}{' '}
          <span className="text-muted" data-id="element-224" data-mp="h6nhyypoaa7v">({vehicle.length})</span>
        </h1>
        <p className="mt-6 max-w-xl text-sm leading-relaxed text-muted" data-id="element-225" data-mp="c8lwy88os0tr">
          {vehicle.summary}
        </p>
      </header>

      <div className="mx-auto max-w-page px-6 md:px-10" data-id="element-226" data-mp="q72c8nkm6zni">
        <div className={vehicle.fit === 'cover' ? 'bg-cream/5' : 'bg-cream'} data-id="element-227" data-mp="m8mky7hi354a">
          <img src={vehicle.render} alt={`${vehicle.name}, a ${vehicle.length} ${vehicle.use.toLowerCase()} vehicle`} style={vehicle.focus ? {
          objectPosition: vehicle.focus
        } : undefined} className={`h-72 w-full md:h-[32rem] ${vehicle.fit === 'cover' ? 'object-cover' : 'object-contain'}`} data-id="element-228" data-mp="3umv6wwpioxnh" />
        </div>
      </div>

      <div className="mx-auto grid max-w-page gap-8 px-6 py-14 md:grid-cols-12 md:px-10 md:py-20" data-id="element-229" data-mp="3ayvwjmyv7sil">
        <div className="md:col-span-4" data-id="element-230" data-mp="36ybuc4zfpypl">
          <h2 className="text-[0.65rem] uppercase tracking-micro text-muted" data-id="element-231" data-mp="36xjzwxhkq70w">
            Specification
          </h2>
        </div>
        <div className="md:col-span-8" data-id="element-232" data-mp="31kloj25bt8mt">
          <dl className="border-t border-line" data-id="element-233" data-mp="31ln8xx88f7h7">
            <div className="flex items-baseline justify-between gap-6 border-b border-line py-4" data-id="element-234" data-mp="31mexwp7bq6yi">
              <dt className="text-[0.65rem] uppercase tracking-micro text-muted" data-id="element-235" data-mp="2mtuvxrvwu5in">
                Length
              </dt>
              <dd className="text-sm text-cream" data-id="element-236" data-mp="3fjtpo0l1uqtn">{vehicle.length}</dd>
            </div>
            <div className="flex items-baseline justify-between gap-6 border-b border-line py-4" data-id="element-237" data-mp="3kjg58gocoj5m">
              <dt className="text-[0.65rem] uppercase tracking-micro text-muted" data-id="element-238" data-mp="35m74wxh2k51r">
                Built for
              </dt>
              <dd className="text-sm text-cream" data-id="element-239" data-mp="3akh37gy3blrl">{vehicle.use}</dd>
            </div>
            <div className="flex items-baseline justify-between gap-6 border-b border-line py-4" data-id="element-240" data-mp="1u6il2ez43m56">
              <dt className="text-[0.65rem] uppercase tracking-micro text-muted" data-id="element-241" data-mp="1z6mr1b2e8yh5">
                Markets
              </dt>
              <dd className="text-sm text-cream" data-id="element-242" data-mp="2feb0wnkq8fjp">
                {vehicle.markets.join(' / ')}
              </dd>
            </div>
            <div className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-2 py-4" data-id="element-243" data-mp="2akgbd3ifx6cp">
              <dt className="text-[0.65rem] uppercase tracking-micro text-muted" data-id="element-244" data-mp="25lc2yf3cbcnf">
                On board
              </dt>
              <dd className="text-sm leading-relaxed text-cream" data-id="element-245" data-mp="2yc3sitpwh31s">
                {vehicle.features.join(' · ')}
              </dd>
            </div>
          </dl>
        </div>
      </div>

      {vehicle.gallery.length > 0 && <section className="mx-auto max-w-page px-6 pb-16 md:px-10 md:pb-24" data-id="element-246" data-mp="2od4vy9iesxv5">
          <h2 className="mb-6 text-[0.65rem] uppercase tracking-micro text-muted" data-id="element-247" data-mp="2jf3bbk5c4qhk">
            On the road
          </h2>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-id="element-248" data-mp="1czu23lg9z0z9">
            {vehicle.gallery.map(image => <img key={image} src={image} alt={`${vehicle.name} on location`} className="h-56 w-full object-cover md:h-64" data-id="element-249" data-mp="17ywhz3bpfwv4" />)}
          </div>
        </section>}

      <div className="mx-auto max-w-page border-t border-line px-6 py-10 md:px-10 md:py-14" data-id="element-250" data-mp="3tlh54t7omzhb">
        <Link to={`/fleet/${next.slug}`} className="group flex items-baseline justify-between gap-6" data-id="element-251" data-mp="269tvu7ss98p">
          <span data-id="element-252" data-mp="74l4w89twpsi">
            <span className="text-[0.65rem] uppercase tracking-micro text-muted" data-id="element-253" data-mp="7536lkf9fw7t">
              Next vehicle
            </span>
            <span className="mt-2 block font-display text-2xl uppercase leading-none transition-colors group-hover:text-lilac md:text-4xl" data-id="element-254" data-mp="b92n430jiv16">
              {next.name}
            </span>
          </span>
          <ArrowRightIcon size={22} className="shrink-0 transition-transform duration-300 group-hover:translate-x-1.5" data-id="element-255" data-mp="l31jsmhuhmlq" />
        </Link>
      </div>

      <EndNote data-id="element-256" data-mp="2mx0rkxvrdlto" />
    </article>;
}